/**
 * Axios API service with MSAL token interceptor.
 * Aligned with opticusdocumentationapp-ui/src/services/api.ts
 *
 * All requests automatically receive:
 *   Authorization: Bearer <idToken>
 *   Consumer-Key:  <from env>
 *   X-requestor:   OHP-Documentation; 820e8d26ff3c6529bfb9
 */
import axios from 'axios';
import { msalInstance } from '../auth/msalConfig';
import { getIdToken, handleSessionExpired, isSessionExpired, SessionExpiredError } from '../auth/tokenManager';
import { API_ENDPOINTS } from '../constants/config';

const CONSUMER_KEY = process.env.REACT_APP_CONSUMER_KEY;

const api = axios.create({
  baseURL: API_ENDPOINTS.backend,
  timeout: 120000,
});

// ── Request Interceptor ──────────────────────────────────────────────────────
api.interceptors.request.use(
  async (config) => {
    // Don't fire requests we already know will 401 — the user is being
    // redirected to sign in and the session-expired banner explains why.
    if (isSessionExpired()) {
      return Promise.reject(new SessionExpiredError('session_expired'));
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      // Throws SessionExpiredError (and starts re-login) when the token can't be renewed.
      const idToken = await getIdToken();
      config.headers.Authorization = `Bearer ${idToken}`;

      if (!config.headers.Accept) {
        config.headers.Accept = 'application/json';
      }
      config.headers['Consumer-Key']  = CONSUMER_KEY;
      config.headers['X-requestor']   = 'OHP-Documentation; 820e8d26ff3c6529bfb9';
    } else {
      console.warn('[api] No MSAL accounts found — request will be sent without auth headers');
    }
    return config;
  },
  (error) => {
    console.error('[api] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// ── Response Interceptor ─────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // `@require_auth` responses always include a `reason` field; those 401s are not an expired MSAL session.
    const isAuthRejection = Boolean(error.response?.data?.reason);
    if (error.response?.status === 401 && !isAuthRejection) {
      console.warn('[api] 401 Unauthorized – session expired');
      handleSessionExpired('http_401');
    }
    return Promise.reject(error);
  }
);

export default api;

